// CONTRACT:C4-DESIGN-TOKENS.1.0
/** status-{hostile,friendly,neutral,unknown,pending} — the SINGLE source of affiliation
 *  color across the map (CONTRACT:C2-RENDER-ENGINE.1.0) and the panels
 *  (CONTRACT:C3-CAPABILITY-PLUGIN.1.0). Colors follow MIL-STD-2525 / APP-6 affiliation
 *  conventions so a hostile track is the same red everywhere it appears. Keys align 1:1
 *  with CONTRACT:P1-CANONICAL-ENVELOPE.1.0 properties.affiliation. */

// Type-only import of the canonical affiliation union (erases at runtime).
import type { Affiliation } from "../../contracts-ts/src/index.ts";
import type { StatusKey } from "./slots.ts";

/** The five affiliation/status names (token suffixes). */
export type StatusName = "hostile" | "friendly" | "neutral" | "unknown" | "pending";

/** Affiliation/status frame-fill colors — the design-system canon palette
 *  (tak-design-system Design System.dc.html §07, the affiliation legend
 *  AFF={friend,hostile,neutral,unknown,suspect}). Affiliation is FUNCTION-HUE
 *  only: these are the canonical primitive hexes; themes reference them via the
 *  status-* slots so a hostile track is the same red everywhere it appears.
 *  Canon ↔ token-name map (the token keeps the MIL-STD-2525 status vocabulary):
 *  - hostile : #FF5256 (canon hostile red)
 *  - friendly: #4DA6FF (canon friend blue)
 *  - neutral : #46D98E (canon neutral green)
 *  - unknown : #F2C14E (canon unknown amber-yellow)
 *  - pending : #FF9E40 (canon SUSPECT orange — the fifth affiliation slot) */
export const APP6_AFFILIATION_COLORS: Readonly<Record<StatusName, string>> = Object.freeze({
  hostile: "#FF5256",
  friendly: "#4DA6FF",
  neutral: "#46D98E",
  unknown: "#F2C14E",
  pending: "#FF9E40",
});

/** Map the canonical P1 Affiliation union (which uses "friend") onto the status token name
 *  ("friendly"). This is the bridge that keeps symbology consistent with the data model. */
export function affiliationToStatus(affiliation: Affiliation): StatusName {
  return affiliation === "friend" ? "friendly" : affiliation;
}

/** Map a status name to its semantic token key (CSS-var name without `--`). */
export function statusKey(name: StatusName): StatusKey {
  return `status-${name}` as StatusKey;
}

/** Convenience: canonical affiliation → status slot key. */
export function affiliationKey(affiliation: Affiliation): StatusKey {
  return statusKey(affiliationToStatus(affiliation));
}

/** All five status names, in canonical order. */
export const STATUS_NAMES: readonly StatusName[] = [
  "hostile",
  "friendly",
  "neutral",
  "unknown",
  "pending",
];
